import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, inject, Input, OnDestroy } from '@angular/core';
import * as echarts from 'echarts';
import { applyRoleVisibilityToOption } from '../../utils/chart-role-visibility.utils';
import { Subscription } from 'rxjs';
import { ConfigService } from '../../shared-services/config.service';
import { ChartFilterHandlerService } from '../../shared-services/chart-filter-handler.service';
import { ChartWrapperService, ChartWrapperInstance } from '../../shared-services/chart-wrapper.service';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';
import { ThemeService } from '../../shared-services/theme.service';
import { buildConfigurableTooltipFormatter, buildConfigurableAxisLabel } from '../../utils/chart.utils';
import { getSeriesColor, ColorConfig } from '../../utils/color.utils';
import { buildSecondaryXAxisOption, buildSecondaryYAxisOption, getDoubleAxisGridAdjustments } from '../../utils/axis.utils';

import { CapitalCasePipe } from '../../pipes/capital-case.pipe';

@Component({
  selector: 'app-stack-bar-chart',
  imports: [CommonModule, CapitalCasePipe, CapitalCasePipe],
  templateUrl: './stack-bar-chart.component.html',
  styleUrl: './stack-bar-chart.component.scss',
})
export class StackBarChartComponent implements AfterViewInit, OnDestroy {
  @Input() data!: any;
  protected myChart!: echarts.ECharts;
  protected allSeriesNames: string[] = [];
  protected allCategories: string[] = [];

  protected wrapper: ChartWrapperInstance = inject(ChartWrapperService).createInstance();
  protected configService = inject(ConfigService);
  protected chartFilterHandler = inject(ChartFilterHandlerService);
  protected responsiveLayoutService = inject(ResponsiveLayoutService);
  protected themeService = inject(ThemeService);
  protected reflowSub?: Subscription;
  protected themeSub?: Subscription;
  private lastChartData?: any;

  ngAfterViewInit(): void {
    const initialFilters = this.wrapper.attach(this.data, {
      onReload: (filters) => this.loadAndRenderChart(filters),
      getChart: () => this.myChart,
      getSeriesNames: () => this.allSeriesNames,
      getCategories: () => this.allCategories,
    });

    setTimeout(() => this.loadAndRenderChart(initialFilters ?? undefined), 0);

    this.reflowSub = this.responsiveLayoutService.reflow$.subscribe(() => {
      this.myChart?.resize();
    });

    // Re-render with fresh theme tokens when the user switches theme at runtime.
    // Dispose + re-init is required because ECharts' registered 'dvm' theme
    // updates do NOT propagate to already-initialized chart instances.
    this.themeSub = this.themeService.theme$.subscribe(() => {
      if (!this.lastChartData || !this.myChart) return;
      const chartDom = document.getElementById(this.data?.widgetId || '');
      if (!chartDom) return;
      this.myChart.dispose();
      this.myChart = echarts.init(chartDom, 'dvm');
      this.initChart(this.lastChartData);
    });
  }

  protected loadAndRenderChart(filters?: any): void {
    const chartDom = document.getElementById(this.data?.widgetId || '');
    if (!chartDom) return;

    if (!this.myChart) {
      this.myChart = echarts.init(chartDom, 'dvm');
    }

    this.myChart.showLoading();

    if (this.data?.data) {
      this.initChart(this.data.data);
    } else if (this.data?.apiUrl) {
      this.configService.getConfig(this.data.apiUrl, this.data, filters).subscribe({
        next: (res) => this.initChart(res?.data || res),
        error: () => this.myChart.hideLoading(),
      });
    } else {
      this.myChart.hideLoading();
    }

    this.addResizeListener();
  }

  protected initChart(chartData: any): void {
    if (!chartData) return;

    this.lastChartData = chartData;

    const theme = this.themeService.resolve(this.data?.themeOverrides);
    const config = this.data?.chartConfig || {};
    let categories: string[] = [];
    let seriesData: any[] = [];

    const dataKey = this.data?.dataKey;
    const seriesKey = this.data?.seriesKey;

    if (dataKey && chartData[dataKey] && Array.isArray(chartData[dataKey])) {
      const flatData = chartData[dataKey];
      // Add combined category key so sourceDataMap can find items by x-axis label (e.g., "Q4 2023")
      flatData.forEach((d: any) => d._category = `${d.quarter} ${d.year}`);
      this.wrapper.setSourceData(flatData, '_category', this.data?.seriesKey || 'series');
      categories = Array.from(new Set(flatData.map((d: any) => d._category)));
      const uniqueSeriesNames = Array.from(new Set(flatData.map((d: any) => d[seriesKey] || 'Unknown')));
      seriesData = uniqueSeriesNames.map((name: any) => {
        const seriesPoints = categories.map((cat) => {
          const point = flatData.find((d: any) => d[seriesKey] === name && `${d.quarter} ${d.year}` === cat);
          return point ? { ...point, value: point.value } : 0;
        });
        return { name, type: 'bar', stack: 'total', emphasis: { focus: 'series' }, data: seriesPoints };
      });
    } else if (!chartData.category && !chartData.series) {
      const seriesNames = Object.keys(chartData);
      if (seriesNames.length > 0) {
        const firstSeriesData = chartData[seriesNames[0]];
        if (Array.isArray(firstSeriesData)) {
          categories = firstSeriesData.map((item: any) => `${item.quarter} ${item.year}`);
        }
        seriesData = seriesNames.map((name) => ({
          name, type: 'bar', stack: 'total', emphasis: { focus: 'series' },
          data: Array.isArray(chartData[name]) ? chartData[name].map((d: any) => d.value) : [],
        }));
      }
    } else if (chartData.category && chartData.series) {
      categories = chartData.category;
      seriesData = chartData.series.map((s: any) => ({
        name: s.name, type: 'bar', stack: 'total', emphasis: { focus: 'series' }, data: s.data,
      }));
    }

    this.allSeriesNames = seriesData.map((s) => s.name);
    this.allCategories = categories;

    // Color consistency — use wrapper
    const colorConfig: ColorConfig = {
      colorMap: config.colorMap || {},
      topNColorsByStackPosition: config.topNColorsByStackPosition || [],
    };
    const coloredSeries = seriesData.map((s, index) => {
      let seriesColor = getSeriesColor(index, s.name, colorConfig);
      seriesColor = this.wrapper.applyColorConsistency(s.name, seriesColor) || seriesColor;
      return { ...s, ...(seriesColor ? { itemStyle: { color: seriesColor } } : {}) };
    });

    const tooltipCfg = this.data?.chartConfig?.tooltip;
    const formatter = buildConfigurableTooltipFormatter(tooltipCfg);
    const gridAdj = getDoubleAxisGridAdjustments(this.data);

    const primaryXAxis: any = { type: 'category', data: categories, triggerEvent: true };
    const secondaryX = buildSecondaryXAxisOption(this.data, categories);
    const xAxisOption = secondaryX ? [primaryXAxis, secondaryX.axis] : primaryXAxis;

    const primaryYAxis: any = {
      type: 'value',
      ...(buildConfigurableAxisLabel(this.data?.chartConfig?.yAxis)
        ? { axisLabel: buildConfigurableAxisLabel(this.data?.chartConfig?.yAxis) }
        : {}),
    };
    const secondaryY = buildSecondaryYAxisOption(this.data);
    const yAxisOption = secondaryY ? [primaryYAxis, secondaryY] : primaryYAxis;

    const option = {
      tooltip: {
        trigger: tooltipCfg?.trigger || 'axis',
        appendToBody: tooltipCfg?.appendToBody ?? true,
        confine: tooltipCfg?.confine ?? false,
        backgroundColor: theme.tooltip?.bg || '#fff',
        borderColor: theme.tooltip?.borderColor || '#ccc',
        textStyle: {
          color: theme.tooltip?.textColor || '#333',
          fontSize: theme.tooltip?.fontSize || 13,
        },
        axisPointer: { type: 'shadow' },
        ...(formatter ? { formatter } : {}),
      },
      legend: {
        data: seriesData.map((s) => s.name),
        bottom: 0,
        type: 'scroll',
      },
      grid: {
        top: '15%',
        left: '2%',
        right: `${4 + gridAdj.extraRight}%`,
        bottom: `${20 + gridAdj.extraBottom}%`,
        containLabel: true,
      },
      xAxis: xAxisOption,
      yAxis: yAxisOption,
      series: coloredSeries,
    };

    if (this.myChart) {
      applyRoleVisibilityToOption(option, this.data);
      this.myChart.setOption(option, true);
      this.myChart.hideLoading();

      this.wrapper.afterRender();
    }
  }

  protected setupChartClickEvent(): void {
    this.myChart?.off('click');
    this.myChart?.on('click', (params: any) => this.updateFilterSelection(params));
  }

  protected updateFilterSelection(params: any): void {
    const selectedValue = params?.name ?? params?.value;
    if (selectedValue !== undefined && selectedValue !== null) {
      this.chartFilterHandler.updateFilterSelection(this.data, selectedValue);
    }
  }

  protected addResizeListener(): void {
    window.addEventListener('resize', this.handleResize);
  }

  protected handleResize = (): void => {
    this.myChart?.resize();
  };

  ngOnDestroy(): void {
    this.wrapper.detach();
    this.reflowSub?.unsubscribe();
    this.themeSub?.unsubscribe();
    window.removeEventListener('resize', this.handleResize);
    this.myChart?.dispose();
  }
}
